import React, { useCallback, useState, useEffect } from 'react';
import * as Yup from 'yup';
import { ErrorMessage, Field, Form, Formik } from 'formik';
import {useDispatch} from 'react-redux';
import {showModal} from '../../../../redux/system/actions';
import ConfirmModal from '../../../common/modals/ConfirmModal';

const CANCEL_MODAL_ID = 'cancelModal';
const phoneRegex = /^(\+?\d{0,4})?\s?-?\s?(\(?\d{3}\)?)\s?-?\s?(\(?\d{3}\)?)\s?-?\s?(\(?\d{4}\)?)?$/

const validationSchema = Yup.object().shape({
    firstName: Yup.string().trim().required('First name is required'),
    lastName: Yup.string().trim().required('Last name is required'),
    enterprise: Yup.string().trim().required('Enterprise is required'),
    jobTitle: Yup.string().trim().required('Job title is required'),
    phoneNumber: Yup.string().matches(phoneRegex, 'Phone number is not valid'),
    mail: Yup.string().email('Invalid email format'),
    }).test('at-least-one', 'One of phone number or mail is required', function (values) {
    const { phoneNumber, mail } = values;
    
    if (!phoneNumber && !mail) {
        return this.createError({
            path: 'phoneNumber',
            message: 'One of phone number or mail is required',
        }) && this.createError({
            path: 'mail',
            message: 'One of phone number or mail is required',
        });
    }

    return true;
});

function ReferenceForm({ curriculumVitaeId, initialReference, setEditingReference, setAddingReference, onSave, status }) {
      const dispatch = useDispatch();
      const handleShowModal = useCallback((modalId) => dispatch(showModal(modalId)), [dispatch]);
    
    const [reference, setReference] = useState(
        initialReference || {
            firstName: '',
            lastName: '',
            enterprise: '',
            jobTitle: '',
            phoneNumber: '',
            mail: '',
            linkedIn: '',
            curriculumVitaeId,
        },
    );

    const clearForm = () => {
        setEditingReference(null);

        setReference({
            firstName: '',
            lastName: '',
            enterprise: '',
            jobTitle: '',
            phoneNumber: '',
            mail: '',
            linkedIn: '',
            curriculumVitaeId,    
        });
    };

    useEffect(() => {
        if(initialReference) {
            setReference({
                ...initialReference
            });
        }
    }, [initialReference]);

    const handleCancel = () => {
        handleShowModal(CANCEL_MODAL_ID);
      };
    
      const confirmCancel = () => {
        setAddingReference(false);
        setEditingReference(null);
      };

    return (
        <><Formik
            enableReinitialize={true}
            initialValues={reference}
            validationSchema={validationSchema}
            onSubmit={(values, { resetForm }) => {
                const updatedReference = {
                    ...values
                };

                onSave(updatedReference);
                clearForm();
                resetForm();
            } }>
            {() => (
                <Form>
                    <div className="row">
                        <div className="col-md-4">
                            <div className="form-group">
                                <label htmlFor="firstName" className="small">
                                    FIRST NAME *
                                </label>
                                <Field name="firstName">
                                {({ field, meta }) => (
                                    <input
                                    {...field}
                                    type="text"
                                    className={`form-control ${meta.touched && meta.error ? 'is-invalid' : ''}`}
                                    placeholder="Enter first name"
                                    />
                                )}
                                </Field>                                  
                                <ErrorMessage name="firstName">
                                    {(msg) => (
                                        <div className="text-danger mt-1" style={{ fontSize: '0.875rem', fontStyle: 'italic' }}>
                                            {msg}
                                        </div>
                                    )}
                                </ErrorMessage>
                            </div>
                        </div>

                        <div className="col-md-4">
                            <div className="form-group">
                                <label htmlFor="lastName" className="small">
                                    LAST NAME *
                                </label>
                                <Field name="lastName">
                                {({ field, meta }) => (
                                    <input
                                    {...field}
                                    type="text"
                                    className={`form-control ${meta.touched && meta.error ? 'is-invalid' : ''}`}
                                    placeholder="Enter last name"
                                    />
                                )}
                                </Field>                                   
                                <ErrorMessage name="lastName">
                                    {(msg) => (
                                        <div className="text-danger mt-1" style={{ fontSize: '0.875rem', fontStyle: 'italic' }}>
                                            {msg}
                                        </div>
                                    )}
                                </ErrorMessage>
                            </div>
                        </div>

                        <div className="col-md-4">
                            <div className="form-group">
                                <label htmlFor="enterprise" className="small">
                                    ENTERPRISE *
                                </label>
                                <Field name="enterprise">
                                {({ field, meta }) => (
                                    <input
                                    {...field}
                                    type="text"
                                    className={`form-control ${meta.touched && meta.error ? 'is-invalid' : ''}`}
                                    placeholder="Enter enterprise"
                                    />
                                )}
                                </Field>                                   
                                <ErrorMessage name="enterprise">
                                    {(msg) => (
                                        <div className="text-danger mt-1" style={{ fontSize: '0.875rem', fontStyle: 'italic' }}>
                                            {msg}
                                        </div>
                                    )}
                                </ErrorMessage>
                            </div>
                        </div>
                    </div>

                    <div className="row">
                        <div className="col-md-4">
                            <div className="form-group">
                                <label htmlFor="jobTitle" className="small">
                                    JOB TITLE *
                                </label>
                                <Field name="jobTitle">
                                {({ field, meta }) => (
                                    <input
                                    {...field}
                                    type="text"
                                    className={`form-control ${meta.touched && meta.error ? 'is-invalid' : ''}`}
                                    placeholder="Enter job title"
                                    />
                                )}
                                </Field>                                   
                                <ErrorMessage name="jobTitle">
                                    {(msg) => (
                                        <div className="text-danger mt-1" style={{ fontSize: '0.875rem', fontStyle: 'italic' }}>
                                            {msg}
                                        </div>
                                    )}
                                </ErrorMessage>
                            </div>
                        </div>

                        <div className="col-md-4">
                            <div className="form-group">
                                <label htmlFor="phoneNumber" className="small">
                                    PHONE NUMBER *
                                </label>
                                <Field name="phoneNumber">
                                {({ field, meta }) => (
                                    <input
                                    {...field}
                                    type="text"
                                    className={`form-control ${meta.touched && meta.error ? 'is-invalid' : ''}`}
                                    placeholder="Enter phone number"
                                    />
                                )}
                                </Field>                                   
                                <ErrorMessage name="phoneNumber">
                                    {(msg) => (
                                        <div className="text-danger mt-1" style={{ fontSize: '0.875rem', fontStyle: 'italic' }}>
                                            {msg}
                                        </div>
                                    )}
                                </ErrorMessage>
                            </div>
                        </div>

                        <div className="col-md-4">
                            <div className="form-group">
                                <label htmlFor="mail" className="small">
                                    MAIL *
                                </label>
                                <Field name="mail">
                                {({ field, meta }) => (
                                    <input
                                    {...field}
                                    type="text"
                                    className={`form-control ${meta.touched && meta.error ? 'is-invalid' : ''}`}
                                    placeholder="Enter e-mail"
                                    />
                                )}
                                </Field>                                   
                                <ErrorMessage name="mail">
                                    {(msg) => (
                                        <div className="text-danger mt-1" style={{ fontSize: '0.875rem', fontStyle: 'italic' }}>
                                            {msg}
                                        </div>
                                    )}
                                </ErrorMessage>
                            </div>
                        </div>
                    </div>

                    <div className="row">
                        <div className="col-md-4">
                            <div className="form-group">
                                <label htmlFor="linkedIn" className="small">
                                    LINKEDIN
                                </label>
                                <Field name="linkedIn" className="form-control" placeholder="Enter linkedIn" />
                            </div>
                        </div>
                    </div>
                    <div className="mb-5">
                        {initialReference ? (
                            <div className="d-flex align-items-start">
                                <button className="btn btn-primary d-flex align-items-center mt-2 mr-3" type="submit">
                                    <i className="pe-7s-pen mr-2"></i>
                                    <span>Update Reference</span>
                                </button>
                                {status === 1 ? (
                                <button className="cancel-button d-flex mt-2" type="button" onClick={handleCancel}>
                                    <span>Cancel</span>
                                </button>
                                ) : (
                                <button className="cancel-button d-flex align-items-center mt-2" type="button" onClick={clearForm}>
                                    <span>Cancel</span>
                                </button>
                                )}
                            </div>
                        ) : (
                            <div className='d-flex'>
                            <button className="btn btn-primary d-flex mt-2 mr-3" type="submit">
                              <i className="pe-7s-plus mr-2"></i>
                              <span>Add Reference</span>
                            </button>
                            {status === 1 && (
                              <button className="cancel-button d-flex mt-2" type="button" onClick={handleCancel}>
                                <span>Cancel</span>
                              </button>
                            )}
                          </div>
                        )}
                    </div>
                </Form>
            )}
        </Formik>
        <ConfirmModal
            id={CANCEL_MODAL_ID}
            title="Are you sure you want to stop adding this reference?"
            message="This will cancel the form and return you to the overview. Do you wish to proceed?"
            onConfirm={confirmCancel} />
        </>
    )
}

export default ReferenceForm;